import React, { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { orderBy, filterBy } from '@progress/kendo-data-query'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Button } from '@progress/kendo-react-buttons'
import { FaFileExcel, FaSearch, FaExchangeAlt } from 'react-icons/fa'
import { useToast } from '../../contexts/ToastContext'
import { getMaterialMovements, MaterialMovement } from '../../data/jobData'
import './MaterialMovementsReport.css'

const getDefaultDates = () => {
  const today = new Date()
  const dayOfWeek = today.getDay() // 0 = Sunday
  // Start of previous week (Monday)
  const startOfPrevWeek = new Date(today)
  startOfPrevWeek.setDate(today.getDate() - dayOfWeek - 6)
  startOfPrevWeek.setHours(0, 0, 0, 0)
  // End of previous week (Sunday)
  const endOfPrevWeek = new Date(today)
  endOfPrevWeek.setDate(today.getDate() - dayOfWeek)
  endOfPrevWeek.setHours(23, 59, 59, 999)
  return { startOfPrevWeek, endOfPrevWeek }
}

function MaterialMovementsReport() {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const { startOfPrevWeek, endOfPrevWeek } = getDefaultDates()

  const [startDate, setStartDate] = useState<Date | null>(startOfPrevWeek)
  const [endDate, setEndDate] = useState<Date | null>(endOfPrevWeek)

  const [data, setData] = useState<MaterialMovement[]>([])
  const [loading, setLoading] = useState(false)

  // Grid state
  const [sort, setSort] = useState<any[]>([{ field: 'movedDate', dir: 'desc' }])
  const [filter, setFilter] = useState<any>(null)
  const [skip, setSkip] = useState(0)
  const [pageSize] = useState(25)

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      showToast('Please select both start and end dates', 'info')
      return
    }

    if (startDate > endDate) {
      showToast('Start date must be before end date', 'error')
      return
    }

    try {
      setLoading(true)
      const movements = await getMaterialMovements(startDate, endDate)
      setData(movements)
      showToast(
        movements.length > 0
          ? `Loaded ${movements.length} movement${movements.length !== 1 ? 's' : ''}`
          : 'No movements found in this period',
        'success'
      )
    } catch (error: any) {
      console.error('Error fetching material movements:', error)
      showToast(error.response?.data?.message || 'Error loading movements', 'error')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, showToast])

  const dataFiltered = filter ? filterBy(data, filter) : data
  const dataSorted = orderBy(dataFiltered, sort)
  const dataPage = dataSorted.slice(skip, skip + pageSize)

  const exportToExcel = () => {
    const exportData = dataSorted.map(row => ({
      'Moved Date': new Date(row.movedDate).toLocaleDateString('en-GB'),
      'Job Cost ID': row.jobCostId,
      'From Job Ref': row.fromJobRef,
      'From Job': row.fromJobDescription ?? '',
      'To Job Ref': row.toJobRef,
      'To Job': row.toJobDescription ?? '',
      'Account Ref': row.accountRef ?? '',
      'Reference': row.reference ?? '',
      'Details': row.details ?? '',
      'Amount (£)': row.amount ?? 0,
      'Moved By': row.movedByEmployee ?? '',
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Material Movements')

    const fileName = `material_movements_${startDate?.toISOString().split('T')[0]}_to_${endDate?.toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
    showToast(`Exported ${exportData.length} rows to ${fileName}`, 'success')
  }

  const totalMovements = dataSorted.length
  const totalAmount = dataSorted.reduce((sum, m) => sum + (m.amount ?? 0), 0)

  const DateCell = (props: any) => (
    <td>{props.dataItem.movedDate ? new Date(props.dataItem.movedDate).toLocaleDateString('en-GB') : '-'}</td>
  )

  const AmountCell = (props: any) => (
    <td style={{ textAlign: 'right' }}>
      {props.dataItem.amount != null
        ? `£${Number(props.dataItem.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '-'}
    </td>
  )

  const FromJobCell = (props: any) => (
    <td>
      <span style={{ fontWeight: 600 }}>{props.dataItem.fromJobRef}</span>
      {props.dataItem.fromJobDescription && (
        <span style={{ marginLeft: '0.4rem', color: '#555', fontSize: '0.85rem' }}>
          — {props.dataItem.fromJobDescription}
        </span>
      )}
    </td>
  )

  const ToJobCell = (props: any) => (
    <td>
      <span style={{ fontWeight: 600 }}>{props.dataItem.toJobRef}</span>
      {props.dataItem.toJobDescription && (
        <span style={{ marginLeft: '0.4rem', color: '#555', fontSize: '0.85rem' }}>
          — {props.dataItem.toJobDescription}
        </span>
      )}
    </td>
  )

  return (
    <div>
      <div style={{ padding: '1rem' }}>
        <div className="material-movements__header">
          <FaExchangeAlt style={{ fontSize: '2rem', marginRight: '1rem', opacity: 0.85 }} />
          <div>
            <h1>Material Movements</h1>
            <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
              Report on job cost movements between jobs
            </p>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          background: '#f5f9fb',
          padding: '1.5rem',
          borderRadius: '8px',
          marginTop: '1rem',
          marginBottom: '1rem',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            alignItems: 'end',
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                From Date:
              </label>
              <DatePicker
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                To Date:
              </label>
              <DatePicker
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <Button
                onClick={fetchData}
                themeColor="primary"
                disabled={loading}
                style={{ width: '100%', height: '40px' }}
              >
                <FaSearch style={{ marginRight: '0.5rem' }} />
                {loading ? 'Loading…' : 'Run Report'}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary & Export */}
        {data.length > 0 && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button onClick={exportToExcel} themeColor="success">
                <FaFileExcel style={{ marginRight: '0.5rem' }} />
                Export to Excel
              </Button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{
                background: '#1e2a4e',
                color: 'white',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Movements</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{totalMovements}</div>
              </div>

              <div style={{
                background: '#21433b',
                color: 'white',
                padding: '1rem',
                borderRadius: '8px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Value Moved</div>
                <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                  £{totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grid */}
        <Grid
          className="material-movements-grid"
          data={dataPage}
          sortable={true}
          sort={sort}
          onSortChange={(e) => { setSort(e.sort); setSkip(0) }}
          filterable={true}
          filter={filter}
          onFilterChange={(e) => { setFilter(e.filter); setSkip(0) }}
          pageable={{ pageSizes: [25, 50, 100] }}
          skip={skip}
          take={pageSize}
          total={dataSorted.length}
          onPageChange={(e) => setSkip(e.page.skip)}
          style={{ minHeight: '400px' }}
        >
          <GridColumn field="movedDate" title="Moved Date" width="130px" cell={DateCell} filterable={false} />
          <GridColumn field="fromJobRef" title="From Job" cell={FromJobCell} filterable={false} />
          <GridColumn field="toJobRef" title="To Job" cell={ToJobCell} filterable={false} />
          <GridColumn field="accountRef" title="Account Ref" width="130px" />
          <GridColumn field="reference" title="Reference" width="140px" />
          <GridColumn field="details" title="Details" />
          <GridColumn field="amount" title="Amount" width="120px" cell={AmountCell} filterable={false} />
          <GridColumn field="movedByEmployee" title="Moved By" width="150px" />
        </Grid>
      </div>
    </div>
  )
}

export default MaterialMovementsReport
